import { combineReducers, configureStore } from '@reduxjs/toolkit';
// import logger from 'redux-logger';
import { createTransform, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import * as Sentry from '@sentry/react';
import { TUserState } from 'typings/userTypes';
import { clearSession } from 'utils/sessionFunctions';
import { clearExperimentLocks } from 'experiments/assignment';
import analyticsService from 'infrastructure/serviceHandlers/Analytics/Analytics';
import allPlansReducers, {
  clearAllPlansData
} from './reducers/allPlanReducers';
import birdPlansReducers, {
  clearBirdPlansData
} from './reducers/birdPlansReducer';
import currentPlanReducers, {
  clearCurrentPlanData
} from './reducers/currentPlanReducer';
import spaceDevicesInDetailReducers, {
  clearSpaceDevices
} from './reducers/deviceInSpaceReducers';
import freeTrialEligibilityReducers, {
  clearFreeTrialEligibility
} from './reducers/freeTrialEligibilityReducers';
import invoiceReducers, { clearInvoices } from './reducers/invoicesReducers';
import oneTimeDataPlanReducer, {
  clearDataPlans
} from './reducers/oneTimeDataPlanReducer';
import paymentMethodsReducers, {
  clearPaymentMethods
} from './reducers/paymentMethodsReducers';
import promotionSkipsReducers, {
  clearPromotionSkips
} from './reducers/promotionSkipsReducers';
import babyPlansReducers, {
  clearBabyPlansData
} from './reducers/babyPlansReducer';
import petPlansReducers, {
  clearPetPlansData
} from './reducers/petPlansReducer';
import spaceReducers, { clearSpaceData } from './reducers/spaceReducers';
import subscriptionReducer, {
  clearSubscriptionData
} from './reducers/subscriptionReducer';
import trailPlansReducers, {
  clearTrailPlansData
} from './reducers/trailPlansReducer';
import userReducers, { clearUser } from './reducers/userReducers';
import myOrdersReducer, { clearMyOrders } from './reducers/myOrdersReducer';

// Keeps `isLoading` out of the persisted `user` slice — a persisted non-idle
// value rehydrates as a stuck loading state. Replaces
// `redux-persist-transform-filter`, whose only use was this one rule and which
// pulled 160 lodash modules (26 KB parsed) into main.js to do it.
const blacklistUserProperties = createTransform(
  (inboundState: TUserState) => {
    const { isLoading, ...persisted } = inboundState;
    return persisted;
  },
  // Restore the field on rehydrate rather than leaving it undefined: the
  // default reconciler replaces the whole `user` slice, so an absent
  // `isLoading` would not fall back to the reducer's initial 'idle'.
  (outboundState) => ({ ...outboundState, isLoading: 'idle' }) as TUserState,
  { whitelist: ['user'] }
);

const persistConfig = {
  key: 'root',
  storage,
  // `myOrders` holds ephemeral server data (purchases + per-subscription
  // invoices). Persisting it rehydrates stale invoices, which makes the billing
  // screen's lazy List-Invoices fetch think data is already loaded and skip the
  // call. Keep it out of storage so each session fetches fresh.
  blacklist: ['myOrders'],
  transforms: [blacklistUserProperties]
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const rootReducer = combineReducers({
  user: userReducers,
  space: spaceReducers,
  subscription: subscriptionReducer,
  allPlans: allPlansReducers,
  currentPlan: currentPlanReducers,
  trial: freeTrialEligibilityReducers,
  spaceDevices: spaceDevicesInDetailReducers,
  promotionSkips: promotionSkipsReducers,
  invoices: invoiceReducers,
  paymentMethods: paymentMethodsReducers,
  birdPlans: birdPlansReducers,
  petPlans: petPlansReducers,
  babyPlans: babyPlansReducers,
  trailPlans: trailPlansReducers,
  dataPlans: oneTimeDataPlanReducer,
  myOrders: myOrdersReducer
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false
    }),
  enhancers: (existingEnhancers) => [...existingEnhancers, sentryReduxEnhancer]
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const clearReduxStates = () => {
  store.dispatch(clearUser());
  store.dispatch(clearSubscriptionData());
  store.dispatch(clearSpaceData());
  store.dispatch(clearSpaceDevices());
  store.dispatch(clearPromotionSkips());
  store.dispatch(clearAllPlansData());
  store.dispatch(clearCurrentPlanData());
  store.dispatch(clearFreeTrialEligibility());
  store.dispatch(clearInvoices());
  store.dispatch(clearPaymentMethods());
  store.dispatch(clearBirdPlansData());
  store.dispatch(clearPetPlansData());
  store.dispatch(clearBabyPlansData());
  store.dispatch(clearTrailPlansData());
  store.dispatch(clearDataPlans());
  store.dispatch(clearMyOrders());
};

export const LogoutUser = () => {
  clearReduxStates();
  localStorage.clear();
  clearSession();
  // The next user in this tab gets their own experiment variants.
  clearExperimentLocks();
  analyticsService.resetFlags();
};
